import React, { useEffect, useState } from 'react';
import { getUsers, createUser, updateUser, deleteUser, linkUserProfile, downloadExport, getSalesmen, getParties } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Users, Plus, Edit2, Trash2, X, Shield, ShieldCheck, Eye, UserCheck, Download, Link2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const ROLES = [
  { value: 'BOSS_ADMIN', label: 'Admin (Full Access)', color: 'bg-violet-100 text-violet-800', icon: ShieldCheck },
  { value: 'SCL_AGENT', label: 'SCL Agent', color: 'bg-blue-100 text-blue-800', icon: UserCheck },
  { value: 'SCRM_PARTY', label: 'Party User', color: 'bg-green-100 text-green-800', icon: Users },
  { value: 'BIA_VIEWER', label: 'Analytics Viewer', color: 'bg-orange-100 text-orange-800', icon: Eye },
];

export default function UserManagementPage() {
  const { user: currentUser, refreshUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'BIA_VIEWER' });
  const [error, setError] = useState('');
  const [exportingUsers, setExportingUsers] = useState(false);
  const [linkingUser, setLinkingUser] = useState(null);
  const [salesmenList, setSalesmenList] = useState([]);
  const [partiesList, setPartiesList] = useState([]);
  const [linkForm, setLinkForm] = useState({ salesman_id: '', party_id: '' });

  const load = () => {
    setLoading(true);
    getUsers().then(r => { setUsers(r.data.users || []); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleExportUsers = async () => {
    setExportingUsers(true);
    try {
      const success = await downloadExport('/api/boss/core/users/export/csv', `users_export_${new Date().toISOString().slice(0,10)}.csv`);
      if (success) toast.success('Users exported as CSV');
      else toast.error('Export failed');
    } catch { toast.error('Export failed'); }
    setExportingUsers(false);
  };

  const openLinkProfile = async (u) => {
    setLinkingUser(u);
    setLinkForm({ salesman_id: '', party_id: '' });
    try {
      const [smRes, pRes] = await Promise.all([
        getSalesmen({}),
        getParties({}),
      ]);
      setSalesmenList(smRes.data.salesmen || smRes.data || []);
      setPartiesList(pRes.data.parties || pRes.data || []);
    } catch {}
  };

  const handleLinkProfile = async () => {
    if (!linkingUser) return;
    try {
      await linkUserProfile(linkingUser._id, linkForm);
      toast.success('Profile linked successfully');
      setLinkingUser(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link profile');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editItem) {
        await updateUser(editItem._id, form);
        // Editing your own account leaves the session's cached name/role
        // (from login) stale until it's explicitly reloaded from the server.
        if (editItem._id === currentUser?._id) await refreshUser();
      } else {
        if (!form.password) { setError('Password is required'); return; }
        await createUser(form);
      }
      setShowForm(false);
      setEditItem(null);
      setForm({ name: '', email: '', password: '', role: 'BIA_VIEWER' });
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error saving user');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    try { await deleteUser(id); load(); } catch (err) { alert('Error'); }
  };

  const startEdit = (u) => {
    setEditItem(u);
    setForm({ name: u.name, email: u.email, password: '', role: u.role });
    setShowForm(true);
  };

  const getRoleInfo = (role) => ROLES.find(r => r.value === role) || ROLES[3];

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-boss-text">User Management</h1>
          <p className="text-boss-text-muted text-sm">{users.length} users</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportUsers}
            disabled={exportingUsers}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-boss-border text-sm hover:bg-boss-surface-2 disabled:opacity-50 transition-colors"
            data-testid="export-users-btn"
          >
            {exportingUsers ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Export CSV
          </button>
          <button
            onClick={() => { setShowForm(true); setEditItem(null); setForm({ name: '', email: '', password: '', role: 'BIA_VIEWER' }); }}
            className="flex items-center gap-2 px-4 py-2 bg-boss-violet text-white rounded-lg text-sm font-medium hover:bg-boss-violet-2"
            data-testid="add-user-btn"
          >
            <Plus size={16} /> Add User
          </button>
        </div>
      </div>

      {/* Role Legend */}
      <div className="flex flex-wrap gap-3">
        {ROLES.map(r => (
          <div key={r.value} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium ${r.color}`}>
            <r.icon size={14} /> {r.label}
          </div>
        ))}
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-boss-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-boss-text">{editItem ? 'Edit User' : 'Create User'}</h3>
            <button onClick={() => { setShowForm(false); setEditItem(null); setError(''); }}><X size={18} /></button>
          </div>
          {error && <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-boss-text mb-1">Full Name *</label>
              <input required value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none" data-testid="user-name-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-boss-text mb-1">Email *</label>
              <input required type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none" data-testid="user-email-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-boss-text mb-1">{editItem ? 'New Password (leave blank to keep)' : 'Password *'}</label>
              <input type="password" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none" data-testid="user-password-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-boss-text mb-1">Role *</label>
              <select value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none" data-testid="user-role-select">
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-2 flex gap-2">
              <button type="submit" className="px-6 py-2 bg-boss-violet text-white rounded-lg text-sm font-medium hover:bg-boss-violet-2" data-testid="user-submit-btn">{editItem ? 'Update' : 'Create'}</button>
              <button type="button" onClick={() => { setShowForm(false); setEditItem(null); }} className="px-6 py-2 border border-boss-border rounded-lg text-sm hover:bg-boss-surface-2">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-boss-border overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="border-b border-boss-border bg-boss-surface-2">
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Name</th>
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Email</th>
              <th className="text-center py-3 px-4 font-medium text-boss-text-muted">Role</th>
              <th className="text-center py-3 px-4 font-medium text-boss-text-muted">Status</th>
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Created</th>
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Linked Profile</th>
              <th className="text-center py-3 px-4 font-medium text-boss-text-muted">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? [...Array(3)].map((_, i) => (
              <tr key={i}><td colSpan={6} className="py-3 px-4"><div className="h-4 bg-boss-surface-3 rounded animate-pulse" /></td></tr>
            )) : users.length === 0 ? (
              <tr><td colSpan={7} className="py-12 text-center text-boss-text-muted">No users found</td></tr>
            ) : users.map(u => {
              const roleInfo = getRoleInfo(u.role);
              return (
                <tr key={u._id} className="border-b border-boss-border/50 hover:bg-boss-surface-2/50">
                  <td className="py-2.5 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-boss-violet/10 flex items-center justify-center">
                        <span className="text-xs font-semibold text-boss-violet">{u.name?.[0]}</span>
                      </div>
                      <span className="font-medium">{u.name}</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-4 text-boss-text-muted">{u.email}</td>
                  <td className="py-2.5 px-4 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${roleInfo.color}`}>
                      <roleInfo.icon size={12} /> {roleInfo.label}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-xs text-boss-text-muted">{formatDate(u.created_at)}</td>
                  <td className="py-2.5 px-4 text-xs">
                    {u.linked_salesman_name && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded mr-1">{u.linked_salesman_name}</span>}
                    {u.linked_party_name && <span className="px-1.5 py-0.5 bg-green-50 text-green-700 rounded">{u.linked_party_name}</span>}
                    {!u.linked_salesman_name && !u.linked_party_name && <span className="text-boss-text-muted">---</span>}
                  </td>
                  <td className="py-2.5 px-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => startEdit(u)} className="p-1 text-boss-violet hover:bg-boss-violet/10 rounded" title="Edit"><Edit2 size={14} /></button>
                      <button onClick={() => openLinkProfile(u)} className="p-1 text-boss-info hover:bg-boss-info/10 rounded" title="Link Profile"><Link2 size={14} /></button>
                      <button onClick={() => handleDelete(u._id)} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Deactivate"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Link Profile Modal */}
      {linkingUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setLinkingUser(null)}>
          <div className="bg-white rounded-xl p-6 shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="link-profile-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-boss-text">Link Profile — {linkingUser.name}</h3>
              <button onClick={() => setLinkingUser(null)}><X size={18} /></button>
            </div>
            <p className="text-sm text-boss-text-muted mb-4">
              Link this user to a salesman or party profile for Self-Service Portal (SSP) access.
            </p>
            {linkingUser.linked_salesman_name && (
              <div className="mb-3 px-3 py-2 bg-blue-50 rounded-lg text-xs text-blue-700">
                Currently linked to salesman: <strong>{linkingUser.linked_salesman_name}</strong>
              </div>
            )}
            {linkingUser.linked_party_name && (
              <div className="mb-3 px-3 py-2 bg-green-50 rounded-lg text-xs text-green-700">
                Currently linked to party: <strong>{linkingUser.linked_party_name}</strong>
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-boss-text-muted mb-1">Link to Salesman</label>
                <select
                  value={linkForm.salesman_id}
                  onChange={e => setLinkForm(f => ({...f, salesman_id: e.target.value}))}
                  className="w-full px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none"
                  data-testid="link-salesman-select"
                >
                  <option value="">-- None --</option>
                  {salesmenList.map(s => <option key={s._id} value={s._id}>{s.salesman_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-boss-text-muted mb-1">Link to Party</label>
                <select
                  value={linkForm.party_id}
                  onChange={e => setLinkForm(f => ({...f, party_id: e.target.value}))}
                  className="w-full px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none"
                  data-testid="link-party-select"
                >
                  <option value="">-- None --</option>
                  {partiesList.map(p => <option key={p._id} value={p._id}>{p.party_name}</option>)}
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleLinkProfile}
                  className="px-6 py-2 bg-boss-violet text-white rounded-lg text-sm font-medium hover:bg-[#5B4CF2] transition-colors"
                  data-testid="link-profile-submit"
                >
                  Link Profile
                </button>
                <button onClick={() => setLinkingUser(null)} className="px-6 py-2 border border-boss-border rounded-lg text-sm hover:bg-boss-surface-2">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
